import React from 'react';
import clsx from 'clsx';
import {
  WorkOrderReturn,
  WorkOrderUpdate,
} from '../../clients/rust-api-client';
import { formatMoney } from '../../utils/billingHelpers';
import { SeverityGlyph } from '../../violation/linear/StatusIcon';
import { formatDay } from '../../violation/linear/violationMeta';
import { PropertyPicker } from '../../violation/linear/PropertyPicker';
import { RowCheckbox } from '../../violation/linear/RowCheckbox';
import { ListSelection } from '../../violation/linear/useListSelection';
import { WorkOrderStatusIcon } from './WorkOrderStatusIcon';
import {
  PRIORITIES,
  STATUSES,
  isPastScheduled,
  normalizeStatus,
  priorityAsSeverity,
  priorityRank,
  shortRef,
  statusLabel,
  titleCase,
} from './workOrderMeta';

const Chip: React.FC<{ className?: string; children: React.ReactNode; title?: string }> = ({
  className,
  children,
  title,
}) => (
  <span
    title={title}
    className={clsx(
      'inline-flex max-w-[12rem] items-center truncate rounded-full border border-line px-2 py-0.5 text-[12px] leading-4 text-fg-muted',
      className
    )}
  >
    {children}
  </span>
);

/**
 * In-row editing: status and priority pickers that save on choose. With
 * `selection`, a checkbox per row. `onUpdate` hears every choice, even of
 * the value the row already has — the caller decides which rows a change
 * reaches, and other checked rows may not have it.
 */
export interface WorkOrderRowEditing {
  canEdit: boolean;
  /** `${workOrderId}:${field}` for each field saving now. */
  saving: ReadonlySet<string>;
  onUpdate: (workOrder: WorkOrderReturn, patch: WorkOrderUpdate, field: string) => void;
  selection?: ListSelection;
}

/** Icon-only chips; `!` because chipClass sets its own padding. */
const rowPickerClass = '!px-1 !py-0.5';

/**
 * A work order as one dense line, in the violation row's grammar:
 * priority bars, reference, status, title, then the secondary facts as
 * chips on the right and the scheduled day last — red once it has passed
 * with the work still open.
 *
 * As on the violation row, the row is not itself a button: a whole-row
 * "open" button sits underneath, and with `editing` the priority and
 * status pickers sit above it.
 *
 * `compact` keeps only the type chip, for half-width panels like Home's,
 * where vendor and cost would crowd out the title.
 */
export const WorkOrderRow: React.FC<{
  workOrder: WorkOrderReturn;
  onOpen: (w: WorkOrderReturn) => void;
  compact?: boolean;
  /** Omit for a read-only row (Home's preview). */
  editing?: WorkOrderRowEditing;
}> = ({ workOrder: w, onOpen, compact = false, editing }) => {
  const late = isPastScheduled(w);
  const ref = shortRef(w.id);
  const status = normalizeStatus(w.status);
  const priority = PRIORITIES[priorityRank(w.priority)] ?? titleCase(w.priority);
  const busy = (field: string) => !!editing?.saving.has(`${w.id}:${field}`);
  const readOnly = !editing?.canEdit;
  const selection = editing?.canEdit ? editing.selection : undefined;
  const selected = !!selection?.selected.has(w.id);

  return (
    <li
      className={clsx(
        'group relative flex items-center gap-3 px-4 py-2 text-[13px] has-[>button:focus-visible]:bg-fg/5',
        selected ? 'bg-accent/[0.08] hover:bg-accent/[0.11]' : 'hover:bg-fg/[0.03]'
      )}
    >
      <button
        type="button"
        onClick={() => onOpen(w)}
        className="absolute inset-0 focus:outline-none"
      >
        <span className="sr-only">
          {`Open ${ref}: ${w.title}. ${statusLabel(w.status)}, priority ${priority}${late ? ', past its scheduled date' : ''}`}
        </span>
      </button>

      {selection && (
        <RowCheckbox
          checked={selected}
          onChange={(on) => {
            const next = new Set(selection.selected);
            if (on) next.add(w.id);
            else next.delete(w.id);
            selection.onChange(next);
          }}
          label={`Select ${ref}`}
        />
      )}

      {editing ? (
        <span className="relative z-10 -mx-1 inline-flex">
          <PropertyPicker
            options={PRIORITIES.map((p) => ({
              value: p,
              label: p,
              icon: <SeverityGlyph severity={priorityAsSeverity(p)} />,
            }))}
            value={priority}
            onChange={(value) => editing.onUpdate(w, { priority: value }, 'priority')}
            placeholder="Change priority…"
            ariaLabel={`Priority of ${ref}: ${priority}${readOnly ? '' : '. Change priority'}`}
            readOnly={readOnly}
            busy={busy('priority')}
            className={rowPickerClass}
            trigger={<SeverityGlyph severity={priorityAsSeverity(w.priority)} />}
          />
        </span>
      ) : (
        <SeverityGlyph
          severity={priorityAsSeverity(w.priority)}
          className="pointer-events-none"
        />
      )}
      <span className="pointer-events-none hidden w-[72px] shrink-0 font-mono text-[12px] text-fg-muted sm:inline">
        {ref}
      </span>
      {editing ? (
        <span className="relative z-10 -mx-1 inline-flex">
          <PropertyPicker
            options={STATUSES.map((s) => ({
              value: s.value,
              label: s.label,
              icon: <WorkOrderStatusIcon status={s.value} />,
            }))}
            value={status}
            onChange={(value) => editing.onUpdate(w, { status: value }, 'status')}
            placeholder="Change status…"
            ariaLabel={`Status of ${ref}: ${statusLabel(w.status)}${readOnly ? '' : '. Change status'}`}
            readOnly={readOnly}
            busy={busy('status')}
            className={rowPickerClass}
            trigger={<WorkOrderStatusIcon status={w.status} />}
          />
        </span>
      ) : (
        <span title={statusLabel(w.status)} className="pointer-events-none inline-flex">
          <WorkOrderStatusIcon status={w.status} />
        </span>
      )}
      <span className="pointer-events-none min-w-0 flex-1 truncate font-medium text-fg">
        {w.title}
      </span>
      <span className="pointer-events-none hidden items-center gap-1.5 md:flex">
        <Chip>{titleCase(w.work_order_type)}</Chip>
        {!compact && w.vendor_name && (
          <Chip className="hidden xl:inline-flex" title={`Vendor: ${w.vendor_name}`}>
            {w.vendor_name}
          </Chip>
        )}
        {!compact && w.estimated_cost && (
          <Chip title="Estimated cost">{formatMoney(w.estimated_cost)}</Chip>
        )}
      </span>
      {w.scheduled_date && (
        <span
          className={clsx(
            'pointer-events-none hidden w-16 shrink-0 text-right text-[12px] sm:inline',
            late ? 'text-red-400' : 'text-fg-muted'
          )}
        >
          {formatDay(w.scheduled_date)}
        </span>
      )}
    </li>
  );
};

export default WorkOrderRow;
